import { useTranslation } from 'react-i18next';
import { Flex, Select, Typography } from 'antd';
import { useCargoTaskStore } from 'shared/stores';
import { ELocationMode } from 'types/location';
import { ETripTaskType } from 'types/trips';
import { CoordinatesForm } from 'components/trips/trip-create-task-modal/components/task-details/coordinates-form';
import { LocationSwitcher } from 'components/trips/trip-create-task-modal/components/task-details/location-switcher';
import { SharedAddressFields } from 'components/trips/trip-create-task-modal/components/task-details/shared-address-fields';
import { WarehouseSelector } from 'components/trips/trip-create-task-modal/components/task-details/warehouse-selector';
import { isAutoAddressAllowed, needLocationForm, needWarehouseSelect } from '../../utils';
import type { ITaskFormProps } from './types';

export const TaskForm: React.FC<ITaskFormProps> = (props) => {
  const { taskTypeOptions } = props;
  const { t } = useTranslation('trip');

  const {
    taskType,
    setTaskType,
    locationMode,
    setLocationMode,
    coordinates,
    setCoordinates,
    address,
    setAddress,
    warehouseId,
    setWarehouseId,
    cargo,
  } = useCargoTaskStore();

  const showWarehouse = taskType && needWarehouseSelect(taskType);
  const showLocation = taskType && needLocationForm(taskType);
  const showFromCargo = taskType && isAutoAddressAllowed(taskType);

  const getCargoAddress = () => {
    if (!cargo) return undefined;
    if (taskType === ETripTaskType.loading) {
      return cargo.loadingAddress;
    } else if (taskType === ETripTaskType.unloading) {
      return cargo.unloadingAddress;
    }
    return undefined;
  };

  const renderLocationForm = () => {
    if (locationMode === ELocationMode.fromCargo) {
      const cargoAddress = getCargoAddress();
      if (cargoAddress) {
        return <SharedAddressFields value={cargoAddress} disabled />;
      }
    }
    if (locationMode === ELocationMode.coordinates) {
      return <CoordinatesForm value={coordinates} onChange={setCoordinates} />;
    }
    return <SharedAddressFields value={address} onChange={setAddress} />;
  };

  return (
    <Flex gap='large' vertical>
      <Flex gap='small' vertical>
        <Typography.Text type='secondary'>{t('create_tasks_step_task_type')}</Typography.Text>
        <Select
          size='large'
          style={{ width: '100%' }}
          value={taskType}
          options={taskTypeOptions}
          placeholder={t('select_task_type')}
          onChange={setTaskType}
        />
      </Flex>

      {showLocation && (
        <>
          <Flex gap='small' vertical>
            <Typography.Text type='secondary'>{t('select_location_mode')}</Typography.Text>
            <LocationSwitcher value={locationMode} showFromCargo={showFromCargo} onChange={setLocationMode} />
          </Flex>

          {renderLocationForm()}
        </>
      )}

      {showWarehouse && (
        <Flex gap='small' vertical>
          <Typography.Text type='secondary'>{t('select_warehouse')}</Typography.Text>
          <WarehouseSelector value={warehouseId} onChange={setWarehouseId} />
        </Flex>
      )}
    </Flex>
  );
};
